import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useTalentNotion, type NotionPage } from '../hooks/useTalentNotion'
import {
  StudioLayout,
  NotionBlockRenderer,
  useToast,
  useConfirm,
} from '../components/ui'
import { post } from '../lib/api'
import { gettext as _ } from '../lib/i18n'
import styles from './NotionPage.module.css'

function fmt(iso?: string | null): string {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleString('ko-KR')
  } catch {
    return iso
  }
}

export default function NotionPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const id = talentId ?? null

  const {
    status,
    loading,
    error,
    connect,
    disconnect,
    reload,
    pages,
    pagesLoading,
    pagesError,
    loadPages,
    blocks,
    blocksLoading,
    blocksError,
    loadBlocks,
  } = useTalentNotion(id)

  const toast = useToast()
  const { confirm } = useConfirm()

  const [token, setToken] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)

  const connected = status?.connected === true

  // Load page list once connected.
  useEffect(() => {
    if (connected) loadPages()
  }, [connected, loadPages])

  // Reset selection when the talent context changes.
  useEffect(() => {
    setSelectedPageId(null)
  }, [id])

  const selectedPage: NotionPage | undefined = pages.find(
    (p) => p.id === selectedPageId,
  )

  function handleSelectPage(page: NotionPage) {
    setSelectedPageId(page.id)
    void loadBlocks(page.id)
  }

  async function handleConnect() {
    if (!token || connecting) return
    setConnecting(true)
    await connect(token, workspaceName)
    setConnecting(false)
    setToken('')
    setWorkspaceName('')
  }

  async function handleDisconnect() {
    if (disconnecting) return
    const ok = await confirm({
      message: _('Disconnect Notion connection?'),
      danger: true,
      confirmText: _('Disconnect'),
    })
    if (!ok) return
    setDisconnecting(true)
    await disconnect()
    setDisconnecting(false)
    setSelectedPageId(null)
    if (!error) toast.success(_('Disconnected'))
  }

  async function handleImport() {
    if (!id || !selectedPage || importing) return
    setImporting(true)
    try {
      await post<unknown>(`/api/v1/talents/${id}/notion/import`, {
        pageId: selectedPage.id,
        title: selectedPage.title,
        kind: 'memo',
      })
      toast.success(_('Imported as source'))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Import failed'))
    } finally {
      setImporting(false)
    }
  }

  // ── Talent not selected ──────────────────────────────────────
  if (!id) {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.emptyInner}>
          <div className={styles.emptyIcon}>🔗</div>
          <div className={styles.emptyTitle}>{_('Select a talent')}</div>
        </div>
      </div>
    )
  }

  // ── Status loading ───────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.spinner} />
      </div>
    )
  }

  // ── Not connected → centered connect form ────────────────────
  if (!connected) {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.connectCard}>
          <h2 className={styles.connectTitle}>{_('Notion Connection')}</h2>

          {error && (
            <div className={styles.errorBanner}>
              <span>{error}</span>
              <button type="button" className={styles.linkBtn} onClick={reload}>
                {_('Retry')}
              </button>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{_('Notion token')}</label>
            <input
              className={styles.formInput}
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="secret_..."
              autoComplete="off"
            />
            <span className={styles.fieldHint}>
              {_('Token is encrypted and never shown again')}
            </span>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {_('Workspace name (optional)')}
            </label>
            <input
              className={styles.formInput}
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="My Workspace"
            />
          </div>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleConnect}
            disabled={!token || connecting}
          >
            {connecting ? _('Connecting...') : _('Connect')}
          </button>

          <p className={styles.helpText}>
            {_('Get your integration token at notion.so/my-integrations')}
          </p>
        </div>
      </div>
    )
  }

  // ── Connected → 3-column workspace ───────────────────────────
  return (
    <StudioLayout
      left={
        <div className={styles.pageTree}>
          <div className={styles.pageTreeHeader}>
            <span className={styles.pageTreeTitle}>{_('Pages')}</span>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={loadPages}
              disabled={pagesLoading}
              aria-label={_('Retry')}
            >
              ↻
            </button>
          </div>

          {pagesLoading ? (
            <div className={styles.pageTreeState}>{_('Loading...')}</div>
          ) : pagesError ? (
            <div className={styles.pageTreeState}>
              <span className={styles.stateError}>{pagesError}</span>
              <button type="button" className={styles.linkBtn} onClick={loadPages}>
                {_('Retry')}
              </button>
            </div>
          ) : pages.length === 0 ? (
            <div className={styles.pageTreeState}>{_('No pages')}</div>
          ) : (
            <div className={styles.pageTreeList}>
              {pages.map((page) => (
                <button
                  key={page.id}
                  type="button"
                  className={`${styles.pageTreeItem} ${
                    page.id === selectedPageId ? styles.pageTreeItemActive : ''
                  }`}
                  onClick={() => handleSelectPage(page)}
                >
                  <span className={styles.pageTreeItemTitle}>
                    {page.title || page.id}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className={styles.connStatus}>
            <span className={styles.connDot} aria-hidden="true" />
            <div className={styles.connStatusText}>
              <span className={styles.connStatusName}>
                {status?.workspaceName || _('Workspace')}
              </span>
              <span className={styles.connStatusLabel}>{_('Connected')}</span>
            </div>
          </div>
        </div>
      }
      right={
        <div className={styles.actionsPanel}>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={handleImport}
            disabled={!selectedPage || importing}
          >
            {importing ? _('Importing...') : _('Import this page as a source')}
          </button>

          <div className={styles.metaList}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{_('Blocks')}</span>
              <span className={styles.metaValue}>
                {selectedPage ? blocks.length : '–'}
              </span>
            </div>
            {status?.connectedAt && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{_('Connected')}</span>
                <span className={styles.metaValue}>{fmt(status.connectedAt)}</span>
              </div>
            )}
          </div>

          <button
            type="button"
            className={styles.disconnectBtn}
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? _('Disconnecting...') : _('Disconnect')}
          </button>
        </div>
      }
    >
      {selectedPage ? (
        <>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbTitle}>{selectedPage.title}</span>
            <a
              className={styles.breadcrumbLink}
              href={selectedPage.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {_('Open in Notion')} ↗
            </a>
          </div>
          <div className={styles.centerBody}>
            {blocksLoading ? (
              <div className={styles.centerState}>
                <div className={styles.spinner} />
                <span>{_('Loading...')}</span>
              </div>
            ) : blocksError ? (
              <div className={styles.centerState}>
                <span className={styles.stateError}>{blocksError}</span>
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={() => selectedPageId && loadBlocks(selectedPageId)}
                >
                  {_('Retry')}
                </button>
              </div>
            ) : blocks.length === 0 ? (
              <div className={styles.centerState}>{_('No pages')}</div>
            ) : (
              <NotionBlockRenderer blocks={blocks} />
            )}
          </div>
        </>
      ) : (
        <div className={styles.centerBody}>
          <div className={styles.centerState}>
            <div className={styles.emptyIcon}>📄</div>
            <div className={styles.emptyTitle}>{_('Select a page')}</div>
          </div>
        </div>
      )}
    </StudioLayout>
  )
}
